import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import API from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useOrganizationContext } from "@/contexts/OrganizationContext";
import { useCampusContext } from "@/contexts/CampusContext";
import { getErrorMessage } from "@/lib/errorHandler";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  FileText, Users, BarChart3, Plus, ArrowRight,
  BookOpen, FolderOpen, Settings, TrendingUp, Zap,
  CheckCircle2, Clock, PenSquare, BarChart2
} from "lucide-react";
import { formatQuizStatus, getQuizStatusColor } from "@/utils/statusFormatter";

function getGreeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
}

function getFirstName(name) {
  if (!name) return "";
  return name.split(" ")[0];
}

// Tiny inline SVG illustration for empty state
function EmptyIllustration() {
  return (
    <svg width="120" height="100" viewBox="0 0 120 100" fill="none" xmlns="http://www.w3.org/2000/svg" className="mx-auto mb-5 opacity-80">
      <rect x="20" y="10" width="80" height="70" rx="8" fill="#EEF2FF" stroke="#C7D2FE" strokeWidth="1.5"/>
      <rect x="32" y="24" width="56" height="6" rx="3" fill="#C7D2FE"/>
      <rect x="32" y="36" width="40" height="5" rx="2.5" fill="#DDE3F8"/>
      <rect x="32" y="47" width="48" height="5" rx="2.5" fill="#DDE3F8"/>
      <rect x="32" y="58" width="32" height="5" rx="2.5" fill="#DDE3F8"/>
      <circle cx="93" cy="75" r="18" fill="#4F46E5"/>
      <path d="M85 75 L91 81 L101 69" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

const STATUS_BORDER = {
  published: "border-l-emerald-400",
  draft: "border-l-slate-300",
  closed: "border-l-red-300",
  archived: "border-l-amber-300",
};

const STATUS_DOT = {
  published: "bg-emerald-400",
  draft: "bg-slate-300",
  closed: "bg-red-400",
  archived: "bg-amber-400",
};

export default function Dashboard() {
  const { user } = useAuth();
  const { selectedOrg, isSuperadmin } = useOrganizationContext();
  const hasPermission = (p) => isSuperadmin || user?.permissions?.[p] === true;
  const canCreateQuiz = hasPermission('can_create_quizzes');
  const canEditQuiz = hasPermission('can_edit_quizzes');
  const canAccessQuizzes = canEditQuiz || canCreateQuiz || hasPermission('can_view_quizzes') || hasPermission('can_delete_quizzes');
  const canManageGroups = hasPermission('can_manage_groups');
  const canViewReports = hasPermission('can_view_reports');
  const canViewAnalytics = hasPermission('can_view_analytics');
  const { selectedCampus, isOrgAdmin, isMultiCampusAdmin } = useCampusContext();
  const [stats, setStats] = useState({ quizzes: 0, groups: 0, submissions: 0, published: 0, avgSubmissions: 0 });
  const [recentQuizzes, setRecentQuizzes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      if (isSuperadmin && !selectedOrg) {
        setStats({ quizzes: 0, groups: 0, submissions: 0, published: 0, avgSubmissions: 0 });
        setRecentQuizzes([]);
        setLoading(false);
        return;
      }

      try {
        const params = {};
        if (isSuperadmin && selectedOrg?.id) params.organization_id = selectedOrg.id;
        if ((isOrgAdmin || isMultiCampusAdmin) && selectedCampus?.id) params.campus_id = selectedCampus.id;

        const [quizzesRes, groupsRes] = await Promise.all([
          API.get("/quizzes", { params }),
          API.get("/groups", { params }),
        ]);

        let quizzes = quizzesRes.data || [];
        let groups = groupsRes.data || [];
        if (!Array.isArray(quizzes)) quizzes = quizzes.quizzes || quizzes.data || [];
        if (!Array.isArray(groups)) groups = groups.groups || groups.data || [];

        const quizzesArray = Array.isArray(quizzes) ? quizzes : [];
        const groupsArray = Array.isArray(groups) ? groups : [];
        const totalSubs = quizzesArray.reduce((sum, q) => sum + (q.submission_count || 0), 0);
        const publishedCount = quizzesArray.filter(q => q.status === "published").length;
        const avgSubmissions = quizzesArray.length > 0 ? Math.round(totalSubs / quizzesArray.length) : 0;

        setStats({
          quizzes: quizzesArray.length,
          groups: groupsArray.length,
          submissions: totalSubs,
          published: publishedCount,
          avgSubmissions,
        });
        setRecentQuizzes(quizzesArray.slice(0, 5));
      } catch (err) {
        console.error("Failed to load dashboard:", err);
      }
      setLoading(false);
    };
    load();
  }, [selectedOrg?.id, isSuperadmin, selectedCampus?.id, isOrgAdmin, isMultiCampusAdmin]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const STAT_CARDS = [
    {
      label: "Total Quizzes",
      value: stats.quizzes,
      sub: `${stats.published} published`,
      subColor: "text-emerald-600",
      icon: FileText,
      iconBg: "bg-indigo-100",
      iconColor: "text-indigo-600",
      accent: "from-indigo-50 to-indigo-100/40",
      border: "border-indigo-200/60",
    },
    {
      label: "Groups",
      value: stats.groups,
      sub: "active groups",
      subColor: "text-slate-500",
      icon: Users,
      iconBg: "bg-emerald-100",
      iconColor: "text-emerald-600",
      accent: "from-emerald-50 to-emerald-100/40",
      border: "border-emerald-200/60",
    },
    {
      label: "Total Submissions",
      value: stats.submissions,
      sub: stats.quizzes > 0 ? `~${stats.avgSubmissions} per quiz` : "no quizzes yet",
      subColor: "text-slate-500",
      icon: BarChart3,
      iconBg: "bg-amber-100",
      iconColor: "text-amber-600",
      accent: "from-amber-50 to-amber-100/40",
      border: "border-amber-200/60",
    },
  ];

  const QUICK_ACTIONS = [
    canCreateQuiz && { label: "New Quiz", desc: "Create an assessment", icon: Plus, to: "/quizzes/new", primary: true },
    canViewReports && { label: "Reports", desc: "View submissions", icon: BarChart2, to: "/reports" },
    canManageGroups && { label: "Groups", desc: "Manage groups", icon: Users, to: "/groups" },
    canViewAnalytics && { label: "Analyse", desc: "Analytics & insights", icon: TrendingUp, to: "/analyse" },
  ].filter(Boolean);

  return (
    <>
      <style>{`

        .dash-root { font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif; }
        .dash-header { font-family: 'Lexend', sans-serif; font-weight: 600; letter-spacing: -0.03em; }

        .dash-stat-card {
          border-radius: 14px;
          padding: 22px 24px;
          transition: transform 0.2s ease, box-shadow 0.2s ease;
          border: 1px solid;
        }
        .dash-stat-card:hover {
          transform: translateY(-3px);
          box-shadow: 0 10px 24px 0 rgb(0 0 0 / 0.07);
        }

        .dash-quiz-row {
          border-left: 3px solid;
          background: white;
          border-radius: 10px;
          padding: 14px 18px;
          transition: transform 0.15s ease, box-shadow 0.15s ease;
          border-top: 1px solid rgb(226 232 240);
          border-right: 1px solid rgb(226 232 240);
          border-bottom: 1px solid rgb(226 232 240);
        }
        .dash-quiz-row:hover {
          transform: translateX(3px);
          box-shadow: 0 4px 12px 0 rgb(0 0 0 / 0.06);
        }

        .dash-action-card {
          border-radius: 12px;
          padding: 16px 18px;
          display: flex;
          align-items: center;
          gap: 12px;
          transition: transform 0.15s ease, box-shadow 0.15s ease;
          text-decoration: none;
        }
        .dash-action-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 16px 0 rgb(0 0 0 / 0.1);
        }

        @keyframes slideUp {
          from { opacity: 0; transform: translateY(16px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .anim-0 { animation: slideUp 0.4s ease both; }
        .anim-1 { animation: slideUp 0.4s ease 0.07s both; }
        .anim-2 { animation: slideUp 0.4s ease 0.14s both; }
        .anim-3 { animation: slideUp 0.4s ease 0.21s both; }
      `}</style>

      <div className="dash-root space-y-8 pb-10" data-testid="dashboard-page">

        {/* Greeting Header */}
        <div className="flex items-start justify-between anim-0">
          <div>
            <p className="text-sm text-slate-500 font-medium mb-1">{getGreeting()}{user?.name ? `, ${getFirstName(user.name)}` : ""} 👋</p>
            <h1 className="dash-header text-2xl text-slate-900">Your Dashboard</h1>
          </div>
          {canCreateQuiz && (
            <Link to="/quizzes/new">
              <Button data-testid="create-quiz-btn" className="bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm">
                <Plus className="w-4 h-4 mr-1.5" /> New Quiz
              </Button>
            </Link>
          )}
        </div>

        {/* Stat Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 anim-1">
          {STAT_CARDS.map((card, i) => (
            <div
              key={i}
              className={`dash-stat-card bg-gradient-to-br ${card.accent} ${card.border}`}
              data-testid={`stat-card-${card.label.toLowerCase().replace(/\s/g, '-')}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className={`w-10 h-10 rounded-xl ${card.iconBg} flex items-center justify-center`}>
                  <card.icon className={`w-5 h-5 ${card.iconColor}`} />
                </div>
              </div>
              <p className="dash-header text-3xl text-slate-900 leading-none mb-1.5">{card.value}</p>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">{card.label}</p>
              <p className={`text-xs font-medium ${card.subColor}`}>{card.sub}</p>
            </div>
          ))}
        </div>

        {/* Quick Actions */}
        {QUICK_ACTIONS.length > 0 && <div className="anim-2">
          <h2 className="dash-header text-base text-slate-700 mb-3">Quick Actions</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {QUICK_ACTIONS.map((action, i) => (
              <Link key={i} to={action.to} className={`dash-action-card ${action.primary ? "bg-indigo-600 text-white hover:bg-indigo-700" : "bg-white border border-slate-200 text-slate-700 hover:border-indigo-300 hover:bg-indigo-50/50"}`}>
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${action.primary ? "bg-white/20" : "bg-indigo-50"}`}>
                  <action.icon className={`w-4 h-4 ${action.primary ? "text-white" : "text-indigo-600"}`} />
                </div>
                <div className="min-w-0">
                  <p className={`text-sm font-semibold truncate ${action.primary ? "text-white" : "text-slate-800"}`}>{action.label}</p>
                  <p className={`text-xs truncate ${action.primary ? "text-indigo-100" : "text-slate-500"}`}>{action.desc}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>}

        {/* Recent Quizzes */}
        <div className="anim-3">
          <div className="flex items-center justify-between mb-4">
            <h2 className="dash-header text-base text-slate-700">Recent Quizzes</h2>
            {canAccessQuizzes && (
              <Link to="/quizzes" className="text-xs text-indigo-600 hover:text-indigo-700 font-semibold flex items-center gap-1 transition-colors">
                View all <ArrowRight className="w-3 h-3" />
              </Link>
            )}
          </div>

          {recentQuizzes.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-2xl text-center py-12 px-6">
              <EmptyIllustration />
              <p className="text-slate-700 font-semibold mb-1">No quizzes yet</p>
              <p className="text-sm text-slate-500 mb-5">Create your first assessment to get started</p>
              {canCreateQuiz && (
                <Link to="/quizzes/new">
                  <Button variant="outline" className="border-indigo-200 text-indigo-700 hover:bg-indigo-50" data-testid="empty-create-quiz-btn">
                    <Plus className="w-4 h-4 mr-2" /> Create Quiz
                  </Button>
                </Link>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              {recentQuizzes.map((quiz) => {
                const borderColor = STATUS_BORDER[quiz.status] || "border-l-slate-300";
                const dotColor = STATUS_DOT[quiz.status] || "bg-slate-300";
                const inner = (
                  <div className={`dash-quiz-row ${borderColor} ${!canEditQuiz ? "cursor-default opacity-75" : ""}`} data-testid={`quiz-card-${quiz.id}`}>
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <div className={`w-2 h-2 rounded-full flex-shrink-0 ${dotColor}`} />
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-semibold text-slate-900 truncate">{quiz.title}</p>
                          <p className="text-xs text-slate-500 mt-0.5">
                            {quiz.question_count || 0} questions · {quiz.submission_count || 0} submissions
                          </p>
                        </div>
                      </div>
                      <Badge className={`border text-xs font-semibold flex-shrink-0 ${getQuizStatusColor(quiz.status)}`}>
                        {formatQuizStatus(quiz.status)}
                      </Badge>
                    </div>
                  </div>
                );
                return canEditQuiz
                  ? <Link key={quiz.id} to={`/quizzes/${quiz.id}/edit`} className="block">{inner}</Link>
                  : <div key={quiz.id}>{inner}</div>;
              })}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
